/**
 * Single-page monitor for the AI agent — combines usage stats and anomaly
 * findings under a tab control so staff doesn't have to navigate between
 * separate pages. The two tab bodies were originally separate pages
 * (`AgentUsagePage` and `AgentAnomaliesPage`) and have been inlined here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';

import clientInboxApi from '@src/api/clientInbox';
import { useMessage } from '@src/context/common';

// =============================================================================
//   USAGE TAB
// =============================================================================

const WINDOW_PRESETS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 180 days' },
  { value: 365, label: 'Last 365 days' },
];

const GROUP_OPTIONS = [
  { value: 'client', label: 'By client' },
  { value: 'parent_company', label: 'By parent company' },
];

const CHANNEL_OPTIONS = [
  { value: 'all', label: 'Chat + Inbox' },
  { value: 'chat', label: 'Chat only' },
  { value: 'inbox', label: 'Inbox only' },
];

const USAGE_COLUMNS_CLIENT = [
  { key: 'client_name', label: 'Client', numeric: false },
  { key: 'parent_company_name', label: 'Parent', numeric: false },
  { key: 'sessions', label: 'Sessions', numeric: true },
  { key: 'user_messages', label: 'User msgs', numeric: true },
  { key: 'assistant_messages', label: 'Agent msgs', numeric: true },
  { key: 'tool_calls', label: 'Tool calls', numeric: true },
  { key: 'total_tokens', label: 'Total tokens', numeric: true },
  { key: 'estimated_cost_usd', label: 'Est. cost', numeric: true },
  { key: 'channel_split', label: 'Channel split', numeric: false, custom: true },
  { key: 'last_message_at', label: 'Last msg', numeric: false },
];

const USAGE_COLUMNS_PARENT = [
  { key: 'parent_company_name', label: 'Parent', numeric: false },
  { key: 'client_count', label: 'Clients', numeric: true },
  { key: 'sessions', label: 'Sessions', numeric: true },
  { key: 'user_messages', label: 'User msgs', numeric: true },
  { key: 'assistant_messages', label: 'Agent msgs', numeric: true },
  { key: 'tool_calls', label: 'Tool calls', numeric: true },
  { key: 'input_tokens', label: 'In tokens', numeric: true },
  { key: 'output_tokens', label: 'Out tokens', numeric: true },
  { key: 'total_tokens', label: 'Total tokens', numeric: true },
  { key: 'estimated_cost_usd', label: 'Est. cost', numeric: true },
];

function costBg(value, palette) {
  const n = Number(value || 0);
  if (n >= palette.high) return 'rgba(244, 67, 54, 0.18)';
  if (n >= palette.medium) return 'rgba(255, 152, 0, 0.18)';
  return undefined;
}

function formatNumber(n) {
  return Number(n || 0).toLocaleString();
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function toCsv(rows, columns) {
  const headers = columns.map((c) => c.label);
  const lines = [headers.join(',')];
  rows.forEach((row) => {
    const cells = columns.map((c) => {
      const v = row[c.key];
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    });
    lines.push(cells.join(','));
  });
  return lines.join('\n');
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function UsageTab() {
  const message = useMessage();
  const [windowDays, setWindowDays] = useState(30);
  const [groupBy, setGroupBy] = useState('client');
  const [channelMode, setChannelMode] = useState('all');
  const [costHigh, setCostHigh] = useState(25);
  const [costMedium, setCostMedium] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('total_tokens');
  const [sortDir, setSortDir] = useState('desc');

  // Drill-in: when a client row is clicked, fetch that client's per-session
  // breakdown and open it in a dialog.
  const [drillClient, setDrillClient] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const openDrill = useCallback((row) => {
    if (groupBy !== 'client' || !row.client_id) return;
    setDrillClient(row);
    setDrillData(null);
    setDrillLoading(true);
    clientInboxApi
      .fetchAgentUsageSessions({ client_id: row.client_id, window_days: windowDays, limit: 50 })
      .then((res) => setDrillData(res.data || null))
      .catch(() => message.error('Unable to load session detail.'))
      .finally(() => setDrillLoading(false));
  }, [groupBy, message, windowDays]);

  const closeDrill = () => {
    setDrillClient(null);
    setDrillData(null);
  };

  const load = useCallback(() => {
    setLoading(true);
    clientInboxApi
      .fetchAgentUsageStats({ window_days: windowDays, group_by: groupBy, channel: channelMode })
      .then((res) => setData(res.data || null))
      .catch(() => message.error('Unable to load usage stats.'))
      .finally(() => setLoading(false));
  }, [channelMode, groupBy, message, windowDays]);

  useEffect(() => { load(); }, [load]);

  const columns = groupBy === 'parent_company' ? USAGE_COLUMNS_PARENT : USAGE_COLUMNS_CLIENT;

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const sorted = [...data.rows];
    sorted.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const aNum = Number(av);
      const bNum = Number(bv);
      let cmp = 0;
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && typeof av !== 'string') {
        cmp = aNum - bNum;
      } else {
        cmp = String(av || '').localeCompare(String(bv || ''));
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [data, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleExport = () => {
    if (!rows.length) return;
    downloadCsv(`agent-usage-${groupBy}-${windowDays}d.csv`, toCsv(rows, columns));
  };

  const totals = data?.totals;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={handleExport} disabled={!rows.length}>
          Export CSV
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Window</InputLabel>
              <Select value={windowDays} label="Window" onChange={(e) => setWindowDays(Number(e.target.value))}>
                {WINDOW_PRESETS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Group by</InputLabel>
              <Select value={groupBy} label="Group by" onChange={(e) => setGroupBy(e.target.value)}>
                {GROUP_OPTIONS.map((g) => (
                  <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Channel</InputLabel>
              <Select value={channelMode} label="Channel" onChange={(e) => setChannelMode(e.target.value)}>
                {CHANNEL_OPTIONS.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Amber threshold ($)"
              type="number"
              value={costMedium}
              onChange={(e) => setCostMedium(Number(e.target.value) || 0)}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label="Red threshold ($)"
              type="number"
              value={costHigh}
              onChange={(e) => setCostHigh(Number(e.target.value) || 0)}
              sx={{ width: 160 }}
            />
            {data ? (
              <Typography variant="caption" color="text.secondary">
                Rates: ${data.input_rate_per_1m}/1M in · ${data.output_rate_per_1m}/1M out
              </Typography>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      {totals ? (
        <Grid container spacing={2}>
          {[
            { label: 'Rows', value: formatNumber(totals.rows) },
            { label: 'Sessions', value: formatNumber(totals.sessions) },
            { label: 'User msgs', value: formatNumber(totals.user_messages) },
            { label: 'Agent msgs', value: formatNumber(totals.assistant_messages) },
            { label: 'Total tokens', value: formatNumber(totals.total_tokens) },
            { label: 'Est. cost', value: `$${formatNumber(totals.estimated_cost_usd)}` },
          ].map((card) => (
            <Grid key={card.label} item xs={6} md={2}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Typography variant="h6" sx={{ mt: 0.25 }}>{card.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : null}

      <Paper variant="outlined">
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !rows.length ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No chat usage in this window yet.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.numeric ? 'right' : 'left'}
                      sortDirection={sortKey === col.key ? sortDir : false}
                    >
                      {col.custom ? (
                        col.label
                      ) : (
                        <TableSortLabel
                          active={sortKey === col.key}
                          direction={sortKey === col.key ? sortDir : 'desc'}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow
                    key={`${row.client_id || row.parent_company_id || idx}-${idx}`}
                    hover
                    onClick={() => openDrill(row)}
                    sx={{
                      bgcolor: costBg(row.estimated_cost_usd, { high: costHigh, medium: costMedium }),
                      cursor: groupBy === 'client' ? 'pointer' : 'default',
                    }}
                  >
                    {columns.map((col) => {
                      if (col.custom && col.key === 'channel_split') {
                        const byChan = row.by_channel || {};
                        const chatTokens = byChan.chat?.total_tokens || 0;
                        const inboxTokens = byChan.inbox?.total_tokens || 0;
                        const total = chatTokens + inboxTokens;
                        if (!total) {
                          return (
                            <TableCell key={col.key} sx={{ color: 'text.disabled' }}>—</TableCell>
                          );
                        }
                        const chatPct = (chatTokens / total) * 100;
                        const inboxPct = 100 - chatPct;
                        return (
                          <TableCell key={col.key} sx={{ minWidth: 160 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{
                                flex: 1, height: 6, bgcolor: 'action.hover',
                                borderRadius: 1, overflow: 'hidden', display: 'flex',
                              }}>
                                <Box sx={{ width: `${chatPct}%`, height: '100%', bgcolor: 'primary.main' }} />
                                <Box sx={{ width: `${inboxPct}%`, height: '100%', bgcolor: 'secondary.main' }} />
                              </Box>
                              <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                {chatPct.toFixed(0)}% / {inboxPct.toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        );
                      }
                      const v = row[col.key];
                      let display;
                      if (col.key === 'estimated_cost_usd') {
                        display = `$${formatNumber(v)}`;
                      } else if (col.key === 'last_message_at') {
                        display = formatDate(v);
                      } else if (col.numeric) {
                        display = formatNumber(v);
                      } else {
                        display = v || '—';
                      }
                      return (
                        <TableCell key={col.key} align={col.numeric ? 'right' : 'left'}>
                          {display}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Drill-in dialog: per-session breakdown for one client. */}
      <Dialog open={!!drillClient} onClose={closeDrill} maxWidth="lg" fullWidth>
        {drillClient ? (
          <>
            <DialogTitle>
              {drillClient.client_name || 'Client'} — session breakdown
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Top {drillData?.session_count || 0} sessions by tokens · last {windowDays} days
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {drillLoading ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : !drillData?.rows?.length ? (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No sessions in this window.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Title / first message</TableCell>
                        <TableCell>POC</TableCell>
                        <TableCell align="right">Msgs</TableCell>
                        <TableCell align="right">Tool calls</TableCell>
                        <TableCell align="right">In</TableCell>
                        <TableCell align="right">Out</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell>Tools used</TableCell>
                        <TableCell>Last activity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drillData.rows.map((s) => {
                        // Deep-link into the live conversation in Guru. Opens in a
                        // new browser tab so staff can keep several convos open and
                        // toggle between them without losing the monitor view.
                        const convoHref = `/guru?session=${s.session_id}`
                          + `&client=${drillClient.client_id}`;
                        return (
                        <TableRow key={s.session_id} hover>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Link
                              component={RouterLink}
                              to={convoHref}
                              target="_blank"
                              rel="noopener"
                              underline="hover"
                              variant="body2"
                              noWrap
                              sx={{ display: 'block' }}
                            >
                              {s.title}
                            </Link>
                            {s.first_user_message ? (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {s.first_user_message}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {s.created_by_name || s.created_by_email ? (
                              <Link
                                component={RouterLink}
                                to={convoHref}
                                target="_blank"
                                rel="noopener"
                                underline="hover"
                                variant="caption"
                              >
                                {s.created_by_name || s.created_by_email}
                              </Link>
                            ) : (
                              <Typography variant="caption">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {(s.user_messages || 0) + (s.assistant_messages || 0)}
                          </TableCell>
                          <TableCell align="right">{s.tool_calls}</TableCell>
                          <TableCell align="right">{formatNumber(s.input_tokens)}</TableCell>
                          <TableCell align="right">{formatNumber(s.output_tokens)}</TableCell>
                          <TableCell align="right">
                            <strong>{formatNumber(s.total_tokens)}</strong>
                          </TableCell>
                          <TableCell align="right">${formatNumber(s.estimated_cost_usd)}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {(s.tools_used || []).slice(0, 4).map((t) => (
                                <Chip
                                  key={t}
                                  label={t.replace(/^fetch_/, '').replace(/^propose_/, '⇧').replace(/_/g, ' ')}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: 10, height: 18 }}
                                />
                              ))}
                              {(s.tools_used || []).length > 4 ? (
                                <Chip label={`+${s.tools_used.length - 4}`} size="small" sx={{ fontSize: 10, height: 18 }} />
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(s.last_message_at)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDrill}>Close</Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Stack>
  );
}

// =============================================================================
//   BY TOPIC TAB — what kinds of questions are driving tokens / cost
// =============================================================================

function ByTopicTab() {
  const message = useMessage();
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    clientInboxApi
      .fetchAgentUsageByTopic({ window_days: windowDays })
      .then((res) => setData(res.data || null))
      .catch(() => message.error('Unable to load topic breakdown.'))
      .finally(() => setLoading(false));
  }, [message, windowDays]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const grandTotal = data?.totals?.total_tokens || 1;

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          What kinds of questions are driving token usage. Each session is attributed to its primary topic
          (the topic whose tools fired most in that session).
        </Typography>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Window</InputLabel>
            <Select value={windowDays} label="Window" onChange={(e) => setWindowDays(Number(e.target.value))}>
              {WINDOW_PRESETS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {data?.totals ? (
        <Grid container spacing={2}>
          {[
            { label: 'Topics', value: formatNumber(data.totals.topics) },
            { label: 'Sessions', value: formatNumber(data.totals.sessions) },
            { label: 'Total tokens', value: formatNumber(data.totals.total_tokens) },
            { label: 'Est. cost', value: `$${formatNumber(data.totals.estimated_cost_usd)}` },
          ].map((card) => (
            <Grid key={card.label} item xs={6} md={3}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Typography variant="h6" sx={{ mt: 0.25 }}>{card.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : null}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !rows.length ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No usage in this window.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Topic</TableCell>
                  <TableCell align="right">Sessions</TableCell>
                  <TableCell align="right">Distinct clients</TableCell>
                  <TableCell align="right">Tool calls</TableCell>
                  <TableCell align="right">Total tokens</TableCell>
                  <TableCell align="right">Est. cost</TableCell>
                  <TableCell align="right">% of total</TableCell>
                  <TableCell>Top tools</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const pct = grandTotal ? (row.total_tokens / grandTotal) * 100 : 0;
                  return (
                    <TableRow key={row.topic} hover>
                      <TableCell>
                        <Typography variant="body2"><strong>{row.topic}</strong></Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(row.sessions)}</TableCell>
                      <TableCell align="right">{formatNumber(row.distinct_clients)}</TableCell>
                      <TableCell align="right">{formatNumber(row.tool_call_count)}</TableCell>
                      <TableCell align="right">{formatNumber(row.total_tokens)}</TableCell>
                      <TableCell align="right">${formatNumber(row.estimated_cost_usd)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <Box
                            sx={{
                              width: 60,
                              height: 6,
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${Math.min(pct, 100)}%`,
                                height: '100%',
                                bgcolor: 'primary.main',
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'right' }}>
                            {pct.toFixed(1)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(row.top_tools || []).slice(0, 4).map((t) => (
                            <Chip
                              key={t.name}
                              label={`${t.name.replace(/^fetch_/, '').replace(/^propose_/, '⇧').replace(/_/g, ' ')} ×${t.count}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 10, height: 18 }}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
}

// =============================================================================
//   ANOMALIES TAB
// =============================================================================

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'actioned', label: 'Actioned' },
  { value: 'dismissed', label: 'Dismissed' },
];

const SEVERITY_FILTERS = [
  { value: '', label: 'All severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const KIND_FILTERS = [
  { value: '', label: 'All kinds' },
  { value: 'off_topic', label: 'Off-topic' },
  { value: 'jailbreak', label: 'Jailbreak' },
  { value: 'malicious', label: 'Malicious' },
  { value: 'cross_client_probe', label: 'Cross-client probe' },
  { value: 'repeated_question', label: 'Repeated question' },
  { value: 'high_volume', label: 'High volume' },
  { value: 'pattern', label: 'Pattern' },
  { value: 'other', label: 'Other' },
];

const SEVERITY_COLORS = {
  critical: 'error', high: 'warning', medium: 'info', low: 'default',
};
const STATUS_COLORS = {
  new: 'primary', reviewed: 'info', actioned: 'success', dismissed: 'default',
};

function AnomaliesTab() {
  const message = useMessage();
  const [filters, setFilters] = useState({ status: 'new', severity: '', kind: '' });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.severity) params.severity = filters.severity;
    if (filters.kind) params.kind = filters.kind;
    clientInboxApi
      .listChatAnomalyFindings(params)
      .then((res) => {
        const data = res.data?.results || res.data || [];
        setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => message.error('Unable to load findings.'))
      .finally(() => setLoading(false));
  }, [filters, message]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (row) => {
    setSelected(row);
    setEditNotes(row.reviewer_notes || '');
  };
  const closeDetail = () => {
    setSelected(null);
    setEditNotes('');
  };
  const saveStatus = (newStatus) => {
    if (!selected) return;
    setSaving(true);
    clientInboxApi
      .updateChatAnomalyFinding(selected.id, { status: newStatus, reviewer_notes: editNotes })
      .then((res) => {
        setRows((prev) => prev.map((r) => (r.id === res.data.id ? res.data : r)));
        message.success(`Marked ${newStatus}.`);
        closeDetail();
      })
      .catch(() => message.error('Could not save.'))
      .finally(() => setSaving(false));
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                {STATUS_FILTERS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Severity</InputLabel>
              <Select
                value={filters.severity}
                label="Severity"
                onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))}
              >
                {SEVERITY_FILTERS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Kind</InputLabel>
              <Select
                value={filters.kind}
                label="Kind"
                onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))}
              >
                {KIND_FILTERS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !rows.length ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No findings for these filters.</Typography>
            <Typography variant="caption" color="text.secondary">
              The analyzer runs weekly (Mondays 7:00 ET).
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Severity</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Detected</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(row)}>
                    <TableCell>
                      <Chip
                        label={row.severity_display}
                        size="small"
                        color={SEVERITY_COLORS[row.severity] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{row.kind_display}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.title}</Typography>
                    </TableCell>
                    <TableCell>{row.client_name || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status_display}
                        size="small"
                        color={STATUS_COLORS[row.status] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={!!selected} onClose={closeDetail} maxWidth="md" fullWidth>
        {selected ? (
          <>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                  label={selected.severity_display}
                  size="small"
                  color={SEVERITY_COLORS[selected.severity] || 'default'}
                />
                <Chip label={selected.kind_display} size="small" />
                <Typography variant="h6" sx={{ ml: 1 }}>{selected.title}</Typography>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                {selected.client_name ? (
                  <Typography variant="body2">
                    <strong>Client:</strong> {selected.client_name}
                  </Typography>
                ) : (
                  <Alert severity="info" variant="outlined">Cross-client finding (no single client).</Alert>
                )}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Summary</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selected.summary}</Typography>
                </Box>
                {selected.suggested_action ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Suggested action</Typography>
                    <Typography variant="body2">{selected.suggested_action}</Typography>
                  </Box>
                ) : null}
                {Array.isArray(selected.evidence?.quotes) && selected.evidence.quotes.length ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Evidence quotes</Typography>
                    <Stack spacing={1}>
                      {selected.evidence.quotes.map((q, i) => (
                        <Paper key={i} variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>"{q}"</Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                ) : null}
                {Array.isArray(selected.evidence?.session_ids) && selected.evidence.session_ids.length ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Session IDs</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selected.evidence.session_ids.join(', ')}
                    </Typography>
                  </Box>
                ) : null}
                <TextField
                  label="Reviewer notes"
                  multiline
                  rows={3}
                  fullWidth
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  size="small"
                />
                {selected.reviewed_at ? (
                  <Typography variant="caption" color="text.secondary">
                    Last reviewed {new Date(selected.reviewed_at).toLocaleString()} by {selected.reviewed_by || '?'}
                  </Typography>
                ) : null}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDetail} disabled={saving}>Close</Button>
              <Button onClick={() => saveStatus('dismissed')} disabled={saving} color="inherit">
                Dismiss
              </Button>
              <Button onClick={() => saveStatus('reviewed')} disabled={saving}>
                Mark reviewed
              </Button>
              <Button onClick={() => saveStatus('actioned')} disabled={saving} variant="contained">
                Mark actioned
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Stack>
  );
}

// =============================================================================
//   MONITOR PAGE — tabs + state
// =============================================================================

function AgentMonitorPage() {
  // Persist tab selection in the URL so refreshes / bookmarks land on the
  // same tab the user was reading.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['usage', 'topics', 'anomalies'];
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'usage';
  const [tab, setTab] = useState(initialTab);

  const onTabChange = (_e, next) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'usage') {
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ mb: 2 }}>AI Chat Monitor</Typography>
      <Tabs
        value={tab}
        onChange={onTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Usage" value="usage" />
        <Tab label="By Topic" value="topics" />
        <Tab label="Anomalies" value="anomalies" />
      </Tabs>
      {tab === 'usage' ? <UsageTab /> : tab === 'topics' ? <ByTopicTab /> : <AnomaliesTab />}
    </Box>
  );
}

export default AgentMonitorPage;
